import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import '../../styles/AssignmentDetailsPage.css';

const AssignmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState({
    text: '',
    status: '',
    audio: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        setLoading(true);
        
        const response = await api.get(`/assignments/${id}`);
        
        if (response.data) {
          const assignmentData = response.data.success 
            ? response.data.data 
            : response.data;
          
          setAssignment(assignmentData);
          
          // If student and has a submission, pre-fill submission data
          if (user?.role === 'student' && assignmentData.submissions) {
            const userSubmission = assignmentData.submissions.find(
              sub => sub.studentId === user._id
            );
            
            if (userSubmission) {
              setSubmission({
                text: userSubmission.text || '',
                status: userSubmission.status || '',
                audio: null
              });
            }
          }
        }
      } catch (err) {
        console.error('Error fetching assignment details:', err);
        setError('Failed to load assignment details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id, user]);

  const handleSubmissionChange = (e) => {
    const { name, value } = e.target;
    setSubmission(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubmission(prev => ({
        ...prev,
        audio: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!submission.text.trim()) {
      alert('Please provide your submission text');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('text', submission.text);
      formData.append('assignmentId', id);
      
      if (submission.audio) {
        formData.append('audio', submission.audio);
      }
      
      await api.post('/assignments/submit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refresh the assignment data
      const response = await api.get(`/assignments/${id}`);
      if (response.data) {
        const assignmentData = response.data.success 
          ? response.data.data 
          : response.data;
        
        setAssignment(assignmentData);
      }
      
      alert('Submission successful!');
    } catch (err) {
      console.error('Error submitting assignment:', err);
      alert('Failed to submit assignment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendFeedback = async (submissionId) => {
    if (!feedback.trim()) {
      alert('Please enter feedback before sending');
      return;
    }
    
    setSendingFeedback(true);
    
    try {
      await api.post(`/assignments/feedback/${submissionId}`, {
        feedback,
        assignmentId: id
      });
      
      // Refresh the assignment data
      const response = await api.get(`/assignments/${id}`);
      if (response.data) {
        const assignmentData = response.data.success 
          ? response.data.data 
          : response.data;
        
        setAssignment(assignmentData);
      }
      
      setFeedback('');
      alert('Feedback sent successfully!');
    } catch (err) {
      console.error('Error sending feedback:', err);
      alert('Failed to send feedback. Please try again.');
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleDeleteAssignment = async () => {
    try {
      await api.delete(`/assignments/${id}`);
      navigate('/dashboard');
    } catch (err) {
      console.error('Error deleting assignment:', err);
      alert('Failed to delete assignment. Please try again.');
      setShowConfirmDelete(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const canEdit = () => {
    if (!assignment || !user) return false;
    
    // Admin and super_admin can edit any assignment
    if (user.role === 'admin' || user.role === 'super_admin') return true;
    
    // Teachers can edit assignments they created
    if (user.role === 'teacher') {
      return assignment.createdBy === user._id;
    }
    
    return false;
  };

  const canSubmit = () => {
    if (!assignment || !user) return false;
    
    // Only students can submit
    return user.role === 'student';
  };

  const canProvideFeedback = () => {
    if (!assignment || !user) return false;
    
    // Admin, super_admin, and teachers can provide feedback
    return user.role === 'admin' || user.role === 'super_admin' || user.role === 'teacher';
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in_progress': return 'status-in-progress';
      case 'pending': return 'status-pending';
      case 'overdue': return 'status-overdue';
      default: return '';
    }
  };

  return (
    <div className="assignment-details-container">
      <div className="header">
        <h1>Assignment Details</h1>
        <div className="header-actions">
          {location.state?.fromParentChildView ? (
            <Link 
              to={`/parent/children/${location.state.childId}`} 
              className="back-btn"
            >
              Back to Child Profile
            </Link>
          ) : (
            <Link to="/assignments" className="back-btn">
              Back to Assignments
            </Link>
          )}
          {canEdit() && (
            <div className="edit-actions">
              <Link 
                to={`/assignments/${id}/edit`} 
                state={{ isEditMode: true, assignmentId: id }}
                className="edit-btn"
              >
                Edit
              </Link>
              <button 
                className="delete-btn"
                onClick={() => setShowConfirmDelete(true)}
              >
                Delete
              </button>
            </div>
          )}
          {canProvideFeedback() && assignment && (
            <Link
              to={`/assignments/${id}/assess`}
              state={{ 
                studentId: assignment.studentId, 
                studentName: assignment.studentName || (assignment.student ? assignment.student.name : "Student"), 
                assignmentDetails: assignment.title || `${assignment.type || ''} ${assignment.surah ? assignment.surah.name : ''} ${assignment.verses || ''}`
              }}
              className="assess-btn"
            >
              Assess Assignment
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading assignment details...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : assignment ? (
        <div className="assignment-details-content">
          <div className="assignment-info-card">
            <div className="assignment-header">
              <div className="assignment-title">
                <h2>{assignment.title}</h2>
                <div className="assignment-meta">
                  <span className={`status-badge ${getStatusClass(assignment.status)}`}>
                    {assignment.status || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-item">
                <span className="label">Description:</span>
                <span className="value">{assignment.description || 'No description provided'}</span>
              </div>
              
              <div className="info-item">
                <span className="label">Class:</span>
                <span className="value">
                  {assignment.halaqaId ? (
                    typeof assignment.halaqaId === 'object' 
                      ? assignment.halaqaId.name 
                      : assignment.halaqaId
                  ) : 'Not assigned'}
                </span>
              </div>
              
              <div className="info-item">
                <span className="label">Assigned Date:</span>
                <span className="value">{formatDate(assignment.assignedDate)}</span>
              </div>
              
              <div className="info-item">
                <span className="label">Due Date:</span>
                <span className="value">{formatDate(assignment.dueDate)}</span>
              </div>
              
              <div className="info-item">
                <span className="label">Created By:</span>
                <span className="value">
                  {assignment.createdBy ? assignment.createdBy.name || assignment.createdBy : 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          {/* Student Submission Section */}
          {canSubmit() && (
            <div className="submission-card">
              <h2>Your Submission</h2>
              
              <form onSubmit={handleSubmit} className="submission-form">
                <div className="form-group">
                  <label htmlFor="text">Submission Text:</label>
                  <textarea
                    id="text"
                    name="text"
                    value={submission.text}
                    onChange={handleSubmissionChange}
                    placeholder="Enter your submission here..."
                    rows="6"
                    required
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="audio">Audio Recording (optional):</label>
                  <input
                    type="file"
                    id="audio"
                    accept="audio/*"
                    onChange={handleAudioChange}
                  />
                  <small className="helper-text">
                    Upload an audio recording of your recitation (mp3, wav, etc.)
                  </small>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Assignment'}
                </button>
              </form>
            </div>
          )}

          {/* Submissions List for Teachers/Admins */}
          {canProvideFeedback() && assignment.submissions && assignment.submissions.length > 0 && (
            <div className="submissions-card">
              <h2>Student Submissions</h2>
              
              <div className="submissions-list">
                {assignment.submissions.map(submission => (
                  <div key={submission._id} className="submission-item">
                    <div className="submission-header">
                      <div className="student-info">
                        <h3>{submission.studentId ? submission.studentId.name : 'Unknown Student'}</h3>
                        <span className="submission-date">
                          Submitted: {formatDate(submission.submittedAt)}
                        </span>
                      </div>
                      <span className={`status-badge ${getStatusClass(submission.status || 'pending')}`}>
                        {submission.status || 'Pending'}
                      </span>
                    </div>
                    
                    <div className="submission-content">
                      <p>{submission.text}</p>
                      
                      {submission.audioUrl && (
                        <div className="audio-player">
                          <h4>Audio Submission:</h4>
                          <audio controls src={submission.audioUrl}>
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      )}
                    </div>
                    
                    {submission.feedback && (
                      <div className="feedback-section">
                        <h4>Feedback:</h4>
                        <p>{submission.feedback}</p>
                      </div>
                    )}
                    
                    <div className="feedback-form">
                      <textarea
                        placeholder="Enter feedback for this submission..."
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        rows="3"
                      ></textarea>
                      <button 
                        onClick={() => handleSendFeedback(submission._id)}
                        disabled={sendingFeedback}
                        className="send-feedback-btn"
                      >
                        {sendingFeedback ? 'Sending...' : 'Send Feedback'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="not-found">Assignment not found</div>
      )}

      {/* Confirmation Modal */}
      {showConfirmDelete && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Delete Assignment</h3>
            <p>Are you sure you want to delete this assignment? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowConfirmDelete(false)}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={handleDeleteAssignment}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentDetailsPage;